import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Truck, AlertTriangle, Plus, Filter, Calendar, MapPin, Loader2,
  Fuel, Gauge, Wrench, ShieldCheck, FileText, Camera, X, Trophy, Activity,
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { uploadToCloudinary, cloudinaryThumb } from "@/lib/cloudinary";
import { SaludFlotaTab, RankingConductoresTab } from "@/components/FlotaSaludTabs";

const nivelColor = {
  critica: "bg-rose-500/30 text-rose-200 border-rose-500/50",
  alta: "bg-amber-500/30 text-amber-200 border-amber-500/50",
  media: "bg-yellow-500/20 text-yellow-200 border-yellow-500/40",
  baja: "bg-zinc-700 text-zinc-300 border-zinc-600",
};

const tipoIcono = {
  itv: ShieldCheck,
  seguro: ShieldCheck,
  mantenimiento: Wrench,
  incidencia: AlertTriangle,
};

export default function Mantenimiento() {
  const [partes, setPartes] = useState([]);
  const [alertas, setAlertas] = useState([]);
  const [vehiculos, setVehiculos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openNuevo, setOpenNuevo] = useState(false);
  const [openCerrar, setOpenCerrar] = useState(null); // parte abierto

  const [filtroVeh, setFiltroVeh] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("");
  const [filtroIncidencia, setFiltroIncidencia] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = [];
      if (filtroVeh) params.push(`vehiculo_id=${filtroVeh}`);
      if (filtroEstado) params.push(`estado=${filtroEstado}`);
      if (filtroIncidencia) params.push("incidencia=true");
      const [pRes, aRes, vRes] = await Promise.all([
        api.get(`/flota/partes${params.length ? "?" + params.join("&") : ""}`),
        api.get("/flota/alertas"),
        api.get("/vehiculos"),
      ]);
      setPartes(pRes.data.items || []);
      setAlertas(aRes.data.items || []);
      setVehiculos(vRes.data || []);
    } catch (e) {
      toast.error("Error cargando partes");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filtroVeh, filtroEstado, filtroIncidencia]);

  const partesAbiertos = useMemo(() => partes.filter((p) => p.estado === "abierto"), [partes]);

  return (
    <div className="p-6 max-w-[1800px] mx-auto" data-testid="page-mantenimiento">
      <PageHeader
        title="Mantenimiento y Uso de Flota"
        subtitle="Partes de uso, alertas, salud de flota y ranking de conductores"
      />

      <Tabs defaultValue="partes" className="w-full">
        <TabsList className="bg-zinc-900 border border-zinc-800 mb-4">
          <TabsTrigger value="partes" data-testid="tab-partes" className="data-[state=active]:bg-amber-500 data-[state=active]:text-zinc-900">
            <FileText className="w-4 h-4 mr-1" /> Partes ({partes.length})
          </TabsTrigger>
          <TabsTrigger value="salud" data-testid="tab-salud" className="data-[state=active]:bg-amber-500 data-[state=active]:text-zinc-900">
            <Activity className="w-4 h-4 mr-1" /> Salud de Flota
          </TabsTrigger>
          <TabsTrigger value="ranking" data-testid="tab-ranking" className="data-[state=active]:bg-amber-500 data-[state=active]:text-zinc-900">
            <Trophy className="w-4 h-4 mr-1" /> Ranking 🏆
          </TabsTrigger>
        </TabsList>

        <TabsContent value="partes">
          <PartesTabContent
            partes={partes} partesAbiertos={partesAbiertos} alertas={alertas}
            vehiculos={vehiculos} loading={loading}
            filtroVeh={filtroVeh} setFiltroVeh={setFiltroVeh}
            filtroEstado={filtroEstado} setFiltroEstado={setFiltroEstado}
            filtroIncidencia={filtroIncidencia} setFiltroIncidencia={setFiltroIncidencia}
            setOpenNuevo={setOpenNuevo} setOpenCerrar={setOpenCerrar}
          />
        </TabsContent>
        <TabsContent value="salud"><SaludFlotaTab /></TabsContent>
        <TabsContent value="ranking"><RankingConductoresTab /></TabsContent>
      </Tabs>

      {openNuevo && <NuevoParteManualDialog onClose={() => setOpenNuevo(false)} onSaved={() => { setOpenNuevo(false); load(); }} vehiculos={vehiculos} />}
      {openCerrar && <CerrarParteDialog parte={openCerrar} onClose={() => setOpenCerrar(null)} onSaved={() => { setOpenCerrar(null); load(); }} />}
    </div>
  );
}


function PartesTabContent({ partes, partesAbiertos, alertas, vehiculos, loading,
                           filtroVeh, setFiltroVeh, filtroEstado, setFiltroEstado,
                           filtroIncidencia, setFiltroIncidencia, setOpenNuevo, setOpenCerrar }) {
  return (
    <>
      {/* ALERTAS */}
      {alertas.length > 0 && (
        <div className="bg-rose-500/5 border border-rose-500/30 rounded-xl p-4 mb-5" data-testid="alertas-block">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-rose-400" />
            <h3 className="font-bold text-rose-200">Alertas activas ({alertas.length})</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {alertas.map((a, i) => {
              const Ico = tipoIcono[a.tipo] || AlertTriangle;
              return (
                <div key={i} className={`border rounded-lg px-3 py-2 flex items-start gap-2 ${nivelColor[a.nivel] || nivelColor.media}`}>
                  <Ico className="w-4 h-4 mt-0.5 shrink-0" />
                  <div className="text-xs flex-1">
                    <div className="font-semibold uppercase tracking-wider text-[10px] opacity-80">{a.tipo} · {a.nivel}</div>
                    <div>{a.mensaje}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* PARTES ABIERTOS */}
      {partesAbiertos.length > 0 && (
        <div className="bg-amber-500/10 border-2 border-amber-500/40 rounded-xl p-4 mb-5" data-testid="partes-abiertos">
          <div className="flex items-center gap-2 mb-2">
            <Truck className="w-5 h-5 text-amber-300 animate-pulse" />
            <h3 className="font-bold text-amber-200">Partes ABIERTOS ({partesAbiertos.length})</h3>
          </div>
          <div className="space-y-2">
            {partesAbiertos.map((p) => (
              <div key={p.id} className="bg-zinc-900/60 rounded p-2 flex items-center justify-between flex-wrap gap-2">
                <div className="text-xs">
                  <b className="text-amber-300">{p.numero}</b> · {p.vehiculo?.nombre} ({p.vehiculo?.matricula})
                  · {p.conductor_nombre} · salida {p.fecha_salida?.slice(0, 16).replace("T", " ")} · {p.km_salida} km
                  {p.uso === "manual" && <span className="ml-2 bg-cyan-500/30 text-cyan-200 text-[9px] uppercase px-1.5 py-0.5 rounded">manual</span>}
                </div>
                <Button size="sm" onClick={() => setOpenCerrar(p)} data-testid={`btn-cerrar-${p.id}`}
                  className="bg-amber-500 hover:bg-amber-600 text-zinc-900 font-bold h-7">
                  Cerrar parte
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TOOLBAR + filtros */}
      <div className="flex items-center gap-2 flex-wrap mb-3" data-testid="toolbar">
        <Button onClick={() => setOpenNuevo(true)} data-testid="btn-nuevo-parte-manual"
          className="bg-emerald-500 hover:bg-emerald-600 text-zinc-900 font-bold">
          <Plus className="w-4 h-4 mr-1" /> Nuevo parte manual
        </Button>
        <div className="text-xs text-zinc-500 flex items-center gap-1 ml-2"><Filter className="w-3 h-3" /> Filtros:</div>
        <select value={filtroVeh} onChange={(e) => setFiltroVeh(e.target.value)} data-testid="filtro-vehiculo"
          className="bg-zinc-900 border border-zinc-800 rounded text-xs px-2 py-1">
          <option value="">Todos los vehículos</option>
          {vehiculos.map((v) => <option key={v.id} value={v.id}>{v.nombre} ({v.matricula})</option>)}
        </select>
        <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)} data-testid="filtro-estado"
          className="bg-zinc-900 border border-zinc-800 rounded text-xs px-2 py-1">
          <option value="">Todos los estados</option>
          <option value="abierto">Abiertos</option>
          <option value="cerrado">Cerrados</option>
        </select>
        <label className="text-xs text-zinc-400 flex items-center gap-1">
          <input type="checkbox" checked={filtroIncidencia} onChange={(e) => setFiltroIncidencia(e.target.checked)}
            data-testid="filtro-incidencia" className="accent-amber-500" />
          Solo con incidencia
        </label>
      </div>

      {/* LISTADO */}
      {loading ? (
        <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin text-amber-500 inline" /></div>
      ) : partes.length === 0 ? (
        <div className="text-center text-zinc-500 py-10">Sin partes registrados con los filtros actuales.</div>
      ) : (
        <div className="space-y-2" data-testid="partes-list">
          {partes.map((p) => (
            <Link to={`/mantenimiento/parte/${p.id}`} key={p.id}
              className="block bg-zinc-900/60 hover:bg-zinc-900 border border-zinc-800 hover:border-amber-500/40 rounded-lg p-3 transition"
              data-testid={`parte-${p.id}`}>
              <div className="flex items-center gap-3 flex-wrap">
                <div>
                  <div className="text-xs font-mono text-amber-300">{p.numero}</div>
                  <div className="text-[9px] uppercase text-zinc-500">{p.fecha_salida?.slice(0, 10)}</div>
                </div>
                <div className="flex-1 min-w-[200px]">
                  <div className="text-sm font-semibold flex items-center gap-2 flex-wrap">
                    <Truck className="w-3.5 h-3.5 text-amber-400" />
                    {p.vehiculo?.nombre} <span className="text-zinc-500 text-xs">({p.vehiculo?.matricula})</span>
                    {p.uso === "manual" && <span className="bg-cyan-500/30 text-cyan-200 text-[9px] uppercase px-1.5 py-0.5 rounded">manual</span>}
                    {p.incidencia_tuvo && <span className="bg-rose-500/30 text-rose-200 text-[9px] uppercase px-1.5 py-0.5 rounded"><AlertTriangle className="w-2.5 h-2.5 inline mr-0.5" />incidencia</span>}
                    {p.repostaje_hizo && <span className="bg-emerald-500/30 text-emerald-200 text-[9px] uppercase px-1.5 py-0.5 rounded"><Fuel className="w-2.5 h-2.5 inline mr-0.5" />repostó</span>}
                  </div>
                  <div className="text-[11px] text-zinc-400">{p.conductor_nombre} · {p.uso === "manual" ? p.motivo_uso : (p.ruta_id ? `Ruta ${p.ruta_id.slice(0, 8)}` : "—")}</div>
                </div>
                <div className="text-right text-xs">
                  <div className="text-zinc-300 font-bold">{p.km_recorridos != null ? `${p.km_recorridos} km` : <span className="text-amber-400">ABIERTO</span>}</div>
                  <div className="text-zinc-500">{p.km_salida} → {p.km_entrada ?? "?"}</div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </>
  );
}


function NuevoParteManualDialog({ onClose, onSaved, vehiculos }) {
  const [conductores, setConductores] = useState([]);
  const [form, setForm] = useState({
    vehiculo_id: "", conductor: "", km_salida: "", motivo_uso: "",
    danos_previos_tiene: false, danos_previos_descripcion: "", danos_previos_fotos: [],
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/flota/conductores").then((r) => setConductores(r.data.items || [])); }, []);

  const submit = async () => {
    if (!form.vehiculo_id) return toast.error("Selecciona vehículo");
    if (!form.conductor) return toast.error("Selecciona conductor");
    if (!form.km_salida) return toast.error("Indica km de salida");
    if (!form.motivo_uso.trim()) return toast.error("Indica motivo de uso");
    setSaving(true);
    try {
      const [tipo, idVal] = form.conductor.split("|");
      const body = {
        vehiculo_id: form.vehiculo_id,
        km_salida: parseFloat(form.km_salida),
        uso: "manual",
        motivo_uso: form.motivo_uso.trim(),
        danos_previos_tiene: form.danos_previos_tiene,
        danos_previos_descripcion: form.danos_previos_tiene ? form.danos_previos_descripcion : null,
        danos_previos_fotos: form.danos_previos_tiene ? form.danos_previos_fotos : [],
      };
      if (tipo === "interno") body.conductor_user_id = idVal;
      else body.conductor_colaborador_id = idVal;
      await api.post("/flota/partes/abrir", body);
      toast.success("Parte manual creado");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error creando parte");
    } finally { setSaving(false); }
  };

  return (
    <ParteDialog title="Nuevo parte manual" onClose={onClose}>
      <Field label="Vehículo*">
        <select value={form.vehiculo_id} onChange={(e) => setForm({ ...form, vehiculo_id: e.target.value })}
          data-testid="nuevo-veh" className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-sm">
          <option value="">— Selecciona —</option>
          {vehiculos.map((v) => <option key={v.id} value={v.id}>{v.nombre} ({v.matricula}) · {v.km_actuales ?? "—"} km</option>)}
        </select>
      </Field>
      <Field label={`Conductor* (${conductores.length} con carnet)`}>
        <select value={form.conductor} onChange={(e) => setForm({ ...form, conductor: e.target.value })}
          data-testid="nuevo-conductor" className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-sm">
          <option value="">— Selecciona —</option>
          {conductores.map((c) => (
            <option key={`${c.tipo}-${c.id_user || c.id_colaborador}`} value={`${c.tipo}|${c.id_user || c.id_colaborador}`}>
              {c.tipo === "interno" ? "👤" : "🤝"} {c.nombre} · carnet {c.carnet_tipo}
            </option>
          ))}
        </select>
        {conductores.length === 0 && (
          <div className="text-[10px] text-amber-400 mt-1">⚠ Sin conductores con carnet registrado. Añade el carnet en Configuración → Usuarios o Colaboradores.</div>
        )}
      </Field>
      <Field label="Km salida*">
        <input type="number" value={form.km_salida} onChange={(e) => setForm({ ...form, km_salida: e.target.value })}
          data-testid="nuevo-km" className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-sm" />
      </Field>
      <Field label="Motivo de uso*">
        <input value={form.motivo_uso} onChange={(e) => setForm({ ...form, motivo_uso: e.target.value })}
          data-testid="nuevo-motivo" placeholder="Ej: Compra material taller, traslado nave, gestiones"
          className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-sm" />
      </Field>
      <DanosPreviosBlock value={form} onChange={(v) => setForm({ ...form, ...v })} />
      <div className="flex justify-end gap-2 mt-3">
        <Button variant="outline" onClick={onClose} data-testid="btn-cancelar">Cancelar</Button>
        <Button onClick={submit} disabled={saving} data-testid="btn-guardar"
          className="bg-emerald-500 hover:bg-emerald-600 text-zinc-900 font-bold">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Crear parte"}
        </Button>
      </div>
    </ParteDialog>
  );
}


export function CerrarParteDialog({ parte, onClose, onSaved }) {
  const [form, setForm] = useState({
    km_entrada: "", incidencia_tuvo: false, incidencia_descripcion: "", incidencia_fotos: [],
    repostaje_hizo: false, repostaje_litros: "", repostaje_importe: "", repostaje_estacion: "",
    notas: "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.km_entrada) return toast.error("Indica km de entrada");
    if (parseFloat(form.km_entrada) < parte.km_salida) return toast.error(`Los km de entrada deben ser ≥ ${parte.km_salida}`);
    setSaving(true);
    try {
      await api.post(`/flota/partes/${parte.id}/cerrar`, {
        km_entrada: parseFloat(form.km_entrada),
        incidencia_tuvo: form.incidencia_tuvo,
        incidencia_descripcion: form.incidencia_tuvo ? form.incidencia_descripcion : null,
        incidencia_fotos: form.incidencia_tuvo ? form.incidencia_fotos : [],
        repostaje_hizo: form.repostaje_hizo,
        repostaje_litros: form.repostaje_hizo ? parseFloat(form.repostaje_litros) : null,
        repostaje_importe: form.repostaje_hizo ? parseFloat(form.repostaje_importe) : null,
        repostaje_estacion: form.repostaje_hizo ? form.repostaje_estacion : null,
        notas: form.notas || null,
      });
      toast.success("Parte cerrado");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al cerrar parte");
    } finally { setSaving(false); }
  };

  return (
    <ParteDialog title={`Cerrar parte ${parte.numero}`} onClose={onClose}>
      <div className="bg-zinc-900/60 rounded p-2 text-xs text-zinc-400 mb-2">
        <b>{parte.vehiculo?.nombre || ""}</b> · Salida {parte.fecha_salida?.slice(0, 16).replace("T", " ")} · {parte.km_salida} km · conductor {parte.conductor_nombre}
      </div>
      <Field label="Km entrada*">
        <input type="number" value={form.km_entrada} onChange={(e) => setForm({ ...form, km_entrada: e.target.value })}
          data-testid="cerrar-km" className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-sm"
          min={parte.km_salida} />
        {form.km_entrada && parseFloat(form.km_entrada) >= parte.km_salida && (
          <div className="text-[10px] text-emerald-400 mt-0.5">Recorridos: {(parseFloat(form.km_entrada) - parte.km_salida).toFixed(1)} km</div>
        )}
      </Field>

      <label className="flex items-center gap-2 text-sm cursor-pointer mt-3">
        <input type="checkbox" checked={form.incidencia_tuvo} onChange={(e) => setForm({ ...form, incidencia_tuvo: e.target.checked })}
          data-testid="cerrar-incidencia" className="w-4 h-4 accent-rose-500" />
        <span>⚠️ ¿Ha habido alguna incidencia con el vehículo?</span>
      </label>
      {form.incidencia_tuvo && (
        <div className="pl-2 border-l-2 border-rose-500/40 space-y-2 mt-2">
          <textarea value={form.incidencia_descripcion} onChange={(e) => setForm({ ...form, incidencia_descripcion: e.target.value })}
            data-testid="cerrar-incidencia-desc" rows={3}
            placeholder="Describe la incidencia (golpe, avería, luz testigo, etc.)"
            className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-sm" />
          <FotosBlock fotos={form.incidencia_fotos} onChange={(fs) => setForm({ ...form, incidencia_fotos: fs })}
            testidPrefix="cerrar-inc-foto" carpeta="dnf/partes_incidencias" />
        </div>
      )}

      <label className="flex items-center gap-2 text-sm cursor-pointer mt-3">
        <input type="checkbox" checked={form.repostaje_hizo} onChange={(e) => setForm({ ...form, repostaje_hizo: e.target.checked })}
          data-testid="cerrar-repostaje" className="w-4 h-4 accent-emerald-500" />
        <span>⛽ ¿Has repostado el vehículo?</span>
      </label>
      {form.repostaje_hizo && (
        <div className="pl-2 border-l-2 border-emerald-500/40 grid grid-cols-3 gap-2 mt-2">
          <Field label="Litros">
            <input type="number" step="0.01" value={form.repostaje_litros} onChange={(e) => setForm({ ...form, repostaje_litros: e.target.value })}
              data-testid="cerrar-litros" className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-sm" />
          </Field>
          <Field label="Importe €">
            <input type="number" step="0.01" value={form.repostaje_importe} onChange={(e) => setForm({ ...form, repostaje_importe: e.target.value })}
              data-testid="cerrar-importe" className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-sm" />
          </Field>
          <Field label="Estación (opcional)">
            <input value={form.repostaje_estacion} onChange={(e) => setForm({ ...form, repostaje_estacion: e.target.value })}
              data-testid="cerrar-estacion" placeholder="Repsol N-IV"
              className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-sm" />
          </Field>
        </div>
      )}

      <Field label="Notas (opcional)">
        <textarea value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })}
          data-testid="cerrar-notas" rows={2}
          className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-sm" />
      </Field>

      <div className="flex justify-end gap-2 mt-3">
        <Button variant="outline" onClick={onClose}>Cancelar</Button>
        <Button onClick={submit} disabled={saving} data-testid="btn-enviar-parte"
          className="bg-amber-500 hover:bg-amber-600 text-zinc-900 font-bold">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FileText className="w-4 h-4 mr-1" /> Enviar parte de vehículo</>}
        </Button>
      </div>
    </ParteDialog>
  );
}


// ============ SUB-COMPONENTES ============
function ParteDialog({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end md:items-center justify-center p-0 md:p-4" onClick={onClose}>
      <div className="bg-zinc-950 border border-zinc-800 rounded-t-xl md:rounded-xl w-full md:max-w-2xl max-h-[95vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-zinc-950 border-b border-zinc-800 p-3 flex items-center justify-between">
          <h2 className="text-lg font-bold">{title}</h2>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-2">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">{label}</label>
      {children}
    </div>
  );
}

export function DanosPreviosBlock({ value, onChange }) {
  return (
    <>
      <label className="flex items-center gap-2 text-sm cursor-pointer mt-3">
        <input type="checkbox" checked={!!value.danos_previos_tiene} onChange={(e) => onChange({ danos_previos_tiene: e.target.checked })}
          data-testid="danos-tiene" className="w-4 h-4 accent-amber-500" />
        <span>🔍 ¿Daños observados antes de la salida?</span>
      </label>
      {value.danos_previos_tiene && (
        <div className="pl-2 border-l-2 border-amber-500/40 space-y-2 mt-2">
          <textarea value={value.danos_previos_descripcion || ""} onChange={(e) => onChange({ danos_previos_descripcion: e.target.value })}
            data-testid="danos-desc" rows={3}
            placeholder="Describe los daños observados (golpe en parachoques, rayón en puerta, etc.)"
            className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-sm" />
          <FotosBlock fotos={value.danos_previos_fotos || []}
            onChange={(fs) => onChange({ danos_previos_fotos: fs })}
            testidPrefix="danos-foto" carpeta="dnf/partes_danos" />
        </div>
      )}
    </>
  );
}

export function FotosBlock({ fotos, onChange, testidPrefix, carpeta }) {
  const [uploading, setUploading] = useState(false);
  const subir = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    const adds = [];
    for (const f of files) {
      try {
        const up = await uploadToCloudinary(f, carpeta, "image");
        adds.push({ url: up.secure_url, public_id: up.public_id });
      } catch (err) { toast.error(`Error subiendo ${f.name}`); }
    }
    onChange([...(fotos || []), ...adds]);
    setUploading(false);
    e.target.value = "";
  };
  return (
    <div>
      <div className="flex items-center gap-2 flex-wrap mb-1">
        <label className="cursor-pointer">
          <input type="file" multiple accept="image/*" capture="environment" className="hidden"
            onChange={subir} disabled={uploading} data-testid={`${testidPrefix}-input`} />
          <span className={`inline-flex items-center gap-1 bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 px-2 py-1 text-xs rounded ${uploading ? "opacity-50" : ""}`}>
            {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Camera className="w-3 h-3" />}
            Añadir fotos ({fotos?.length || 0})
          </span>
        </label>
      </div>
      {(fotos || []).length > 0 && (
        <div className="grid grid-cols-4 gap-1.5">
          {fotos.map((f, i) => (
            <div key={i} className="relative">
              <img src={cloudinaryThumb(f.url, 120, 90)} alt="" className="w-full h-16 object-cover rounded border border-zinc-800" />
              <button onClick={() => onChange(fotos.filter((_, j) => j !== i))}
                data-testid={`${testidPrefix}-quitar-${i}`}
                className="absolute top-0.5 right-0.5 bg-rose-500 text-white rounded-full p-0.5">
                <X className="w-2.5 h-2.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
